import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./InventoryModal.module.css";
import logo from '../../../../img/logo rio verde pdf.png';
import { API_BASE_URL } from "../../../../config";
import { generateInventoryPDFReport, generateInventoryExcelReport } from './reportGeneratorInventory';
import Swal from 'sweetalert2';
import { FaTrash, FaPencilAlt } from 'react-icons/fa';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const initialInventoryState = {
    numeroInventario: "",
    data: new Date().toISOString().slice(0, 10),
    quantidadeAmostras: "",
    quantidadeArvores: "",
    pesoKgM3: "",
    diametroMedio: "",
    alturaMedia: "",
    volumeTotalM3: "",
    volumeTotalTON: "", 
    volumeLenha: "",
    volume15a20: "",
    volume20a25: "",
    volume25a33: "",
    volume33Acima: "",
    valorLenha: "",
    valor15a20: "",
    valor20a25: "",
    valor25a33: "",
    valor33Acima: "",
    valorTotal: "", 
};

const labelMap = {
    numeroInventario: "Número do Inventário *",
    data: "Data *",
    quantidadeAmostras: "Qtd. Amostras",
    quantidadeArvores: "Árvores/ha",
    pesoKgM3: "Peso (kg/m³)",
    diametroMedio: "Diâmetro Médio (cm)",
    alturaMedia: "Altura Média (m)",
    volumeTotalM3: "Volume Total (m³)",
    volumeTotalTON: "Volume Total (TON) (Auto)", 
    volumeLenha: "Volume Lenha (TON)",
    volume15a20: "Volume 15-20cm (TON)",
    volume20a25: "Volume 20-25cm (TON)",
    volume25a33: "Volume 25-33cm (TON)",
    volume33Acima: "Volume +33cm (TON)",
    valorLenha: "Valor Lenha (R$)",
    valor15a20: "Valor 15-20cm (R$)",
    valor20a25: "Valor 20-25cm (R$)",
    valor25a33: "Valor 25-33cm (R$)",
    valor33Acima: "Valor +33cm (R$)",
    valorTotal: "Valor Total (R$) (Auto)", 
};

const formatCurrency = (value) => parseFloat(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatNumber = (value, fractionDigits = 2) => {
    const num = parseFloat(value || 0);
    if (isNaN(num)) return 'N/A';
    return num.toLocaleString('pt-BR', { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits });
};

const DetailRow = ({ inv }) => {
    const details = [
        { label: 'Qtd. Amostras', value: formatNumber(inv.quantidade_amostras, 0) },
        { label: 'Peso (kg/m³)', value: formatNumber(inv.peso_kg_m3) },
        { label: 'Volume Lenha (TON)', value: formatNumber(inv.volume_lenha) },
        { label: 'Valor Lenha (R$)', value: formatCurrency(inv.valor_lenha) },
        { label: 'Volume 15-20cm (TON)', value: formatNumber(inv.volume_15_a_20) },
        { label: 'Valor 15-20cm (R$)', value: formatCurrency(inv.valor_15_a_20) },
        { label: 'Volume 20-25cm (TON)', value: formatNumber(inv.volume_20_a_25) },
        { label: 'Valor 20-25cm (R$)', value: formatCurrency(inv.valor_20_a_25) },
        { label: 'Volume 25-33cm (TON)', value: formatNumber(inv.volume_25_a_33) },
        { label: 'Valor 25-33cm (R$)', value: formatCurrency(inv.valor_25_a_33) },
        { label: 'Volume +33cm (TON)', value: formatNumber(inv.volume_33_acima) },
        { label: 'Valor +33cm (R$)', value: formatCurrency(inv.valor_33_acima) },
    ];

    return (
        <tr className={styles.detailsRow}>
            <td colSpan="12" className={styles.detailsCell}>
                <div className={styles.detailsContent}>
                    <table className={styles.detailsTable}>
                        <tbody>
                            {details.map((item, index) => (
                                index % 2 === 0 && (
                                    <tr key={item.label}>
                                        <th>{item.label}</th>
                                        <td>{item.value}</td>
                                        {details[index + 1] && (
                                            <>
                                                <th>{details[index + 1].label}</th>
                                                <td>{details[index + 1].value}</td>
                                            </>
                                        )}
                                    </tr>
                                )
                            ))}
                        </tbody>
                    </table>
                </div>
            </td>
        </tr>
    );
};


const InventoryModal = ({ isOpen, onClose, imovel }) => {
    const [inventoryData, setInventoryData] = useState(initialInventoryState);
    const [inventarios, setInventarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [selectedInventarios, setSelectedInventarios] = useState([]);
    const [expandedRows, setExpandedRows] = useState([]); 
    const [editingId, setEditingId] = useState(null);
    const navigate = useNavigate();
    const [chartData, setChartData] = useState(null);
    const chartRef = useRef(null);

    const calculatedTon = useMemo(() => {
        const vol = parseFloat(inventoryData.volumeTotalM3) || 0;
        return vol * 0.95;
    }, [inventoryData.volumeTotalM3]);

    const calculatedTotal = useMemo(() => {
        const v1 = parseFloat(inventoryData.valorLenha) || 0;
        const v2 = parseFloat(inventoryData.valor15a20) || 0;
        const v3 = parseFloat(inventoryData.valor20a25) || 0;
        const v4 = parseFloat(inventoryData.valor25a33) || 0;
        const v5 = parseFloat(inventoryData.valor33Acima) || 0;
        return v1 + v2 + v3 + v4 + v5;
    }, [
        inventoryData.valorLenha, inventoryData.valor15a20, 
        inventoryData.valor20a25, inventoryData.valor25a33, 
        inventoryData.valor33Acima
    ]);

    useEffect(() => {
        if (selectedInventarios.length > 0) {
            const dataForChart = {
                'Lenha': { volume: 0, valor: 0 }, '15-20cm': { volume: 0, valor: 0 },
                '20-25cm': { volume: 0, valor: 0 }, '25-33cm': { volume: 0, valor: 0 },
                '+33cm': { volume: 0, valor: 0 },
            };
            const selectedFullData = inventarios.filter(inv => selectedInventarios.includes(inv.id));
            selectedFullData.forEach(inv => {
                dataForChart['Lenha'].volume += parseFloat(inv.volume_lenha || 0);
                dataForChart['15-20cm'].volume += parseFloat(inv.volume_15_a_20 || 0);
                dataForChart['20-25cm'].volume += parseFloat(inv.volume_20_a_25 || 0);
                dataForChart['25-33cm'].volume += parseFloat(inv.volume_25_a_33 || 0);
                dataForChart['+33cm'].volume += parseFloat(inv.volume_33_acima || 0);
                dataForChart['Lenha'].valor += parseFloat(inv.valor_lenha || 0);
                dataForChart['15-20cm'].valor += parseFloat(inv.valor_15_a_20 || 0);
                dataForChart['20-25cm'].valor += parseFloat(inv.valor_20_a_25 || 0);
                dataForChart['25-33cm'].valor += parseFloat(inv.valor_25_a_33 || 0);
                dataForChart['+33cm'].valor += parseFloat(inv.valor_33_acima || 0);
            });
            setChartData(dataForChart);
        } else {
            setChartData(null);
        }
    }, [selectedInventarios, inventarios]);

    const fetchInventarios = useCallback(async () => {
        if (!imovel || !imovel.id) return;
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) { navigate('/'); return; }
        try {
            const response = await fetch(`${API_BASE_URL}/api/imoveis/${imovel.id}/inventario`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok && response.status !== 404) { 
                throw new Error("Falha ao carregar."); 
            }
            const data = await response.json();
            setInventarios(Array.isArray(data) ? data : []);
        } catch (err) { setError(err.message); setInventarios([]); }
        finally { setLoading(false); }
    }, [imovel, navigate]);

    useEffect(() => { if (isOpen) { fetchInventarios(); } }, [isOpen, fetchInventarios]);
    
    const handleInputChange = (e) => setInventoryData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    
    const handleSubmit = async () => {
        setIsSaving(true);
        setError(null);

        const { numeroInventario, data } = inventoryData;
        if (!numeroInventario || !data) {
            const msg = "Número do Inventário e Data são obrigatórios.";
            setError(msg);
            Swal.fire({
                title: 'Atenção!',
                text: msg,
                icon: 'warning',
                confirmButtonColor: '#1e8449'
            });
            setIsSaving(false);
            return;
        }

        const dataToSave = {
            ...inventoryData,
            imovel_id: imovel.id, 
            volumeTotalTON: calculatedTon.toFixed(2),
            valorTotal: calculatedTotal.toFixed(2),
        };
        
        if (editingId) {
            await handleUpdate(dataToSave);
        } else {
            await handleCreate(dataToSave);
        }
        
        setIsSaving(false);
    };

    const handleCreate = async (dataToSave) => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${API_BASE_URL}/api/imoveis/${imovel.id}/inventario`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify(dataToSave),
            });
            if (!response.ok) {
                 const errData = await response.json();
                 throw new Error(errData.error || "Não foi possível salvar.");
            }
            
            Swal.fire({
                title: 'Sucesso!',
                text: 'Inventário salvo com sucesso!',
                icon: 'success',
                confirmButtonColor: '#1e8449'
            });
            setInventoryData(initialInventoryState);
            await fetchInventarios();
        } catch (err) { 
            setError(err.message); 
            Swal.fire({
                title: 'Erro!',
                text: err.message,
                icon: 'error',
                confirmButtonColor: '#d33'
            });
        }
    };
    
    const handleUpdate = async (dataToSave) => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${API_BASE_URL}/api/inventario/${editingId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify(dataToSave),
            });
            if (!response.ok) {
                 const errData = await response.json();
                 throw new Error(errData.error || "Não foi possível atualizar.");
            }
            
            Swal.fire({
                title: 'Sucesso!',
                text: 'Inventário atualizado com sucesso!',
                icon: 'success',
                confirmButtonColor: '#1e8449'
            });
            setInventoryData(initialInventoryState);
            setEditingId(null);
            await fetchInventarios();
        } catch (err) { 
            setError(err.message); 
            Swal.fire({
                title: 'Erro!',
                text: err.message,
                icon: 'error',
                confirmButtonColor: '#d33'
            });
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Tem certeza?',
            text: "Você não poderá reverter isso!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#1e8449',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sim, excluir!',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            setIsSaving(true); 
            setError(null);
            const token = localStorage.getItem('token');
            
            try {
                const response = await fetch(`${API_BASE_URL}/api/inventario/${id}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                
                if (!response.ok) {
                    const errData = await response.json();
                    throw new Error(errData.error || "Não foi possível excluir.");
                }
                
                Swal.fire({
                    title: 'Excluído!',
                    text: 'O inventário foi excluído.',
                    icon: 'success',
                    confirmButtonColor: '#1e8449'
                });
                
                await fetchInventarios();
                
            } catch (err) {
                setError(err.message);
                Swal.fire({
                    title: 'Erro!',
                    text: err.message,
                    icon: 'error',
                    confirmButtonColor: '#d33'
                });
            } finally {
                setIsSaving(false);
            }
        }
    };
    
    const handleEditClick = (inv) => {
        const formattedDate = new Date(inv.data).toISOString().slice(0, 10);
        
        setInventoryData({
            numeroInventario: inv.numero_inventario || "",
            data: formattedDate,
            quantidadeAmostras: inv.quantidade_amostras || "",
            quantidadeArvores: inv.quantidade_arvores || "",
            pesoKgM3: inv.peso_kg_m3 || "",
            diametroMedio: inv.diametro_medio || "",
            alturaMedia: inv.altura_media || "",
            volumeTotalM3: inv.volume_total_m3 || "",
            volumeTotalTON: inv.volume_total_ton || "",
            volumeLenha: inv.volume_lenha || "",
            volume15a20: inv.volume_15_a_20 || "",
            volume20a25: inv.volume_20_a_25 || "",
            volume25a33: inv.volume_25_a_33 || "",
            volume33Acima: inv.volume_33_acima || "",
            valorLenha: inv.valor_lenha || "",
            valor15a20: inv.valor_15_a_20 || "",
            valor20a25: inv.valor_20_a_25 || "",
            valor25a33: inv.valor_25_a_33 || "",
            valor33Acima: inv.valor_33_acima || "",
            valorTotal: inv.valor_total || "",
        });
        setEditingId(inv.id);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setInventoryData(initialInventoryState);
        setError(null);
    };

    const handleSelectInventario = (e, id) => {
        e.stopPropagation(); 
        setSelectedInventarios(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
    };
    
    const handleToggleExpand = (id) => {
        setExpandedRows(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
    };

    const formatDateForDisplay = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return new Date(date.getTime() + (date.getTimezoneOffset() * 60000)).toLocaleDateString("pt-BR");
    };
    
    const handleGenerateReport = () => {
        const selectedData = inventarios.filter(inv => selectedInventarios.includes(inv.id));
        const chartImage = chartRef.current ? chartRef.current.toBase64Image() : null;
        generateInventoryPDFReport({ selectedData, logo, chartImage, imovel });
        generateInventoryExcelReport({ selectedData, imovel });
    };

    if (!isOpen) return null;

    const chartOptions = { responsive: true, plugins: { legend: { position: 'top' }, title: { display: true, font: { size: 16 } } } };
    const doughnutData = {
        labels: chartData ? Object.keys(chartData) : [],
        datasets: [{
            label: 'Volume (TON)',
            data: chartData ? Object.values(chartData).map(d => d.volume) : [],
            backgroundColor: ['#2ecc71', '#3498db', '#f1c40f', '#e67e22', '#e74c3c'],
        }],
    };
    const barData = {
        labels: chartData ? Object.keys(chartData) : [],
        datasets: [{
            label: 'Valor Total (R$)',
            data: chartData ? Object.values(chartData).map(d => d.valor) : [],
            backgroundColor: 'rgba(22, 160, 133, 0.7)',
        }],
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <header className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>Gestão de Inventários</h2>
                    <span className={styles.modalSubtitle}>{imovel?.descricao || `Imóvel ID: ${imovel?.id || '...'}`}</span>
                    <button className={styles.modalCloseBtn} onClick={onClose}>&times;</button>
                </header>
                <main className={styles.modalBody}>
                    <div className={styles.mainContent}>
                        <div className={styles.card}>
                             <h3 className={styles.sectionTitle}>Inventários Salvos</h3>
                             {loading ? (<p>Carregando...</p>) : 
                             inventarios.length === 0 ? (<p>Nenhum inventário encontrado.</p>) : (
                                <div className={styles.tableResponsive}>
                                    <table className={styles.inventoryTable}>
                                        <thead>
                                            <tr>
                                                <th></th>
                                                <th>Sel.</th>
                                                <th>Número</th>
                                                <th>Data</th>
                                                <th>Árvores/ha</th>
                                                <th>Ø Médio (cm)</th>
                                                <th>Altura Média (m)</th>
                                                <th>Vol. Total (m³)</th>
                                                <th>Vol. Total (TON)</th>
                                                <th>Valor Total (R$)</th>
                                                <th>Editar</th>
                                                <th>Excluir</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {inventarios.map((inv) => (
                                                <React.Fragment key={inv.id}>
                                                    <tr className={styles.expandableRow} onClick={() => handleToggleExpand(inv.id)}>
                                                        <td>
                                                            <span className={styles.expandIcon}>
                                                                {expandedRows.includes(inv.id) ? '▾' : '▸'}
                                                            </span>
                                                        </td>
                                                        <td onClick={(e) => e.stopPropagation()}>
                                                            <input 
                                                                type="checkbox" 
                                                                checked={selectedInventarios.includes(inv.id)} 
                                                                onChange={(e) => handleSelectInventario(e, inv.id)} 
                                                            />
                                                        </td>
                                                        <td>{inv.numero_inventario}</td>
                                                        <td>{formatDateForDisplay(inv.data)}</td>
                                                        <td>{formatNumber(inv.quantidade_arvores)}</td>
                                                        <td>{formatNumber(inv.diametro_medio)}</td>
                                                        <td>{formatNumber(inv.altura_media)}</td>
                                                        <td>{formatNumber(inv.volume_total_m3)}</td>
                                                        <td>{formatNumber(inv.volume_total_ton)}</td>
                                                        <td>{formatCurrency(inv.valor_total)}</td>
                                                        <td onClick={(e) => e.stopPropagation()}>
                                                            <button
                                                                className={styles.editBtn}
                                                                onClick={() => handleEditClick(inv)}
                                                                title="Editar inventário"
                                                            >
                                                                <FaPencilAlt />
                                                            </button>
                                                        </td>
                                                        <td onClick={(e) => e.stopPropagation()}>
                                                            <button 
                                                                className={styles.deleteBtn}
                                                                onClick={() => handleDelete(inv.id)}
                                                                title="Excluir inventário"
                                                            >
                                                                <FaTrash />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    {expandedRows.includes(inv.id) && <DetailRow inv={inv} />}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                    <aside className={styles.sidebar}>
                        <div className={styles.card}>
                            <h3 className={styles.sectionTitle}>
                                {editingId ? "Editar Inventário" : "Adicionar Novo Inventário"}
                            </h3>
                            <div className={styles.formGrid}>
                                {Object.keys(labelMap).map((key) => {
                                    const isCalculated = key === 'volumeTotalTON' || key === 'valorTotal';
                                    let displayValue = inventoryData[key];
                                    if (key === 'volumeTotalTON') {
                                        displayValue = calculatedTon.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                    } else if (key === 'valorTotal') {
                                        displayValue = calculatedTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
                                    }

                                    return (
                                        <div 
                                            className={styles.inputGroup} 
                                            key={key} 
                                            data-span={(key === 'numeroInventario' || key === 'valorTotal' || key === 'volumeTotalTON') ? '2' : '1'}
                                        >
                                            <label className={styles.formLabel}>{labelMap[key] || key}</label>
                                            <input
                                                type={isCalculated ? "text" : (key.includes("data") ? "date" : (key === "numeroInventario" ? "text" : "number"))}
                                                name={key}
                                                value={isCalculated ? displayValue : inventoryData[key]}
                                                onChange={!isCalculated ? handleInputChange : undefined}
                                                className={styles.formInput}
                                                step="0.01"
                                                disabled={isCalculated}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            <div className={styles.formActions}>
                                {editingId && (
                                    <button onClick={handleCancelEdit} className={`${styles.modalBtn} ${styles.modalBtnCancelForm}`}>
                                        Cancelar
                                    </button>
                                )}
                                <button onClick={handleSubmit} className={`${styles.modalBtn} ${styles.modalBtnSuccess}`} disabled={isSaving}>
                                    {isSaving ? "Salvando..." : (editingId ? "Atualizar" : "Salvar Inventário")}
                                </button>
                            </div>
                            {error && <p className={styles.errorMessage}>{error}</p>}
                        </div>

                        {chartData && (
                            <div className={styles.card}>
                                <h3 className={styles.sectionTitle}>Análise da Seleção</h3>
                                <div className={styles.chartsGrid}>
                                    <div className={styles.chartContainer}>
                                        <Doughnut ref={chartRef} data={doughnutData} options={{...chartOptions, plugins: {...chartOptions.plugins, title: {...chartOptions.plugins.title, text: 'Composição de Volume (TON)'}}}} />
                                    </div>
                                    <div className={styles.chartContainer}>
                                        <Bar data={barData} options={{...chartOptions, plugins: {...chartOptions.plugins, title: {...chartOptions.plugins.title, text: 'Composição de Valor (R$)'}}}} />
                                    </div>
                                </div>
                            </div>
                        )}
                    </aside>
                </main>
                <footer className={styles.modalFooter}>
                    <button className={`${styles.modalBtn} ${styles.modalBtnCancel}`} onClick={onClose}>Fechar</button>
                    <button className={`${styles.modalBtn} ${styles.modalBtnReport}`} onClick={handleGenerateReport} disabled={selectedInventarios.length === 0}>
                        Gerar Relatórios (PDF e Excel)
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default InventoryModal;